import { eventBus } from "../../Common/EventBus";

export interface TourStep {
  /** `data-tour` value of the element to highlight; without one the step shows centred on screen. */
  target?: string;
  title: string;
  body: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /** The panel the target lives in, opened for the step and closed once the tour moves out of it. */
  panel?: TourPanel;
}

export interface TourPanel {
  /** `data-tour` of the panel itself, to tell when it has rendered. */
  target: string;
  open: () => void;
  close: () => void;
}

export interface Tour {
  /** What the backend stores once the user finishes or dismisses the tour, `^[a-z0-9-]{1,16}$`. */
  id: string;
  /** Route patterns the tour may start on, matched as a whole path. */
  routes: string[];
  steps: TourStep[];
}

const USER_MENU: TourPanel = {
  target: "user-menu-panel",
  open: () => eventBus.emit("OPEN_USER_MENU"),
  close: () => eventBus.emit("CLOSE_USER_MENU"),
};

export const WELCOME_TOUR: Tour = {
  id: "welcome",
  routes: ["/", "/chat/*", "/due-diligence", "/contracts", "/search"],
  steps: [
    {
      title: "common.tour.welcome.intro.title",
      body: "common.tour.welcome.intro.body",
    },
    {
      target: "nav-copilot",
      title: "common.tour.welcome.copilot.title",
      body: "common.tour.welcome.copilot.body",
      side: "right",
    },
    {
      target: "nav-dueDiligence",
      title: "common.tour.welcome.dueDiligence.title",
      body: "common.tour.welcome.dueDiligence.body",
      side: "right",
    },
    {
      target: "nav-contracts",
      title: "common.tour.welcome.contracts.title",
      body: "common.tour.welcome.contracts.body",
      side: "right",
    },
    {
      target: "user-menu",
      title: "common.tour.welcome.userMenu.title",
      body: "common.tour.welcome.userMenu.body",
      side: "right",
      align: "end",
    },
    // Team and subscription are only in the menu of the business's admins, the
    // rest get these two skipped.
    {
      target: "team",
      title: "common.tour.welcome.team.title",
      body: "common.tour.welcome.team.body",
      side: "right",
      panel: USER_MENU,
    },
    {
      target: "subscription",
      title: "common.tour.welcome.subscription.title",
      body: "common.tour.welcome.subscription.body",
      side: "right",
      panel: USER_MENU,
    },
    {
      target: "pdf-settings",
      title: "common.tour.welcome.pdfSettings.title",
      body: "common.tour.welcome.pdfSettings.body",
      side: "right",
      panel: USER_MENU,
    },
  ],
};

/** In priority order: when several could start on the same page, the first one wins. */
export const TOURS: Tour[] = [WELCOME_TOUR];

export const tourTargetSelector = (target: string) => `[data-tour="${target}"]`;

/**
 * Resolves once the target is in the page, or once the wait is over without it.
 *
 * @param target `data-tour` value to wait for.
 * @param timeoutMs How long to wait at most.
 */
export const waitForTarget = (target: string, timeoutMs: number) =>
  new Promise<void>((resolve) => {
    const startedAt = performance.now();
    const check = () => {
      if (document.querySelector(tourTargetSelector(target)) || performance.now() - startedAt > timeoutMs) {
        resolve();
      } else {
        requestAnimationFrame(check);
      }
    };
    check();
  });

/**
 * The steps a tour can show right now: the ones without a target, plus the ones
 * whose target is on screen (the sidebar, for one, is hidden on mobile). A step
 * in a panel is kept as it is: its target only shows up once the panel opens.
 *
 * @param steps The tour's steps.
 * @returns The steps to show, or an empty list when none of the targeted ones
 *   is on screen: a tour reduced to its loose text is not worth showing, nor
 *   marking as done.
 */
export const visibleSteps = (steps: TourStep[]): TourStep[] => {
  const isOnScreen = (target: string) =>
    Array.from(document.querySelectorAll(tourTargetSelector(target))).some(
      (element) => element.getClientRects().length > 0
    );

  const shown = steps.filter((step) => !step.target || step.panel || isOnScreen(step.target));

  return shown.some((step) => step.target && !step.panel) ? shown : [];
};
